import React, { useState, useEffect } from 'react';
import { Card, Badge, Button, Alert, Modal, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { groupReviewsByVisit } from '../utils/reviewGrouping';
import VisitReviewGroupCard from './VisitReviewGroupCard';
import DishPhoto from './DishPhoto';

const REPORT_REASONS = [
  { value: 'spam', label: 'Spam or advertising' },
  { value: 'offensive', label: 'Offensive or hateful' },
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'other', label: 'Other' },
];

function ReviewList({ restaurantId, foodId, menuItemId, refresh }) {
  const [reviews, setReviews] = useState([]);
  const [aggregation, setAggregation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reportTarget, setReportTarget] = useState(null); // review being reported
  const [reportReason, setReportReason] = useState('spam');
  const [reportDetails, setReportDetails] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportError, setReportError] = useState('');
  const [reportSuccess, setReportSuccess] = useState('');
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  useEffect(() => {
    fetchReviews();
    fetchAggregation();
  }, [restaurantId, foodId, menuItemId, refresh, token]);

  const openReportModal = (review) => {
    setReportTarget(review);
    setReportReason('spam');
    setReportDetails('');
    setReportError('');
  };

  // Report one public visit photo (#91) → backend auto-hides it (post-moderation),
  // then refresh so it drops out of the strip.
  const handleReportPhoto = async (photoId) => {
    if (!window.confirm('Report this photo as inappropriate? It will be hidden pending review.')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/photos/${photoId}/report`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to report photo');
      }
      fetchReviews();
    } catch (e) {
      alert(e.message || 'Failed to report photo');
    }
  };

  const handleReportSubmit = async () => {
    if (!reportTarget) return;
    setReportSubmitting(true);
    setReportError('');
    try {
      const response = await fetch(`${API_BASE_URL}/reviews/${reportTarget.id}/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          reason: reportReason,
          details: reportDetails.trim() || null,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to submit report');
      }

      setReportTarget(null);
      setReportSuccess('Thanks for the report. The review has been hidden pending moderation.');
      fetchReviews();
      fetchAggregation();
    } catch (err) {
      setReportError(err.message || 'Failed to submit report');
    } finally {
      setReportSubmitting(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const params = new URLSearchParams();
      if (restaurantId) params.append('restaurant_id', restaurantId);
      if (foodId) params.append('food_id', foodId);
      if (menuItemId) params.append('menu_item_id', menuItemId);

      // Send auth when available so the backend filters out blocked users' reviews
      const response = await fetch(`${API_BASE_URL}/reviews/?${params}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch reviews: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setReviews(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch reviews');
    } finally {
      setLoading(false);
    }
  };

  const fetchAggregation = async () => {
    try {
      const params = new URLSearchParams();
      if (restaurantId) params.append('restaurant_id', restaurantId);
      if (foodId) params.append('food_id', foodId);
      if (menuItemId) params.append('menu_item_id', menuItemId);

      const response = await fetch(`${API_BASE_URL}/reviews/stats/aggregated?${params}`);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to fetch aggregation:', errorText);
        return; // Don't throw, just log - aggregation is optional
      }

      const data = await response.json();
      setAggregation(data);
    } catch (err) {
      console.error('Failed to fetch aggregation:', err.message || err);
    }
  };


  const renderStars = (rating) => {
    return (
      <div className="star-display">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            style={{
              color: star <= rating ? '#ffc107' : '#e4e5e9',
              fontSize: '1.2rem',
            }}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  const renderRatingDistribution = () => {
    if (!aggregation || aggregation.total_reviews === 0) return null;

    return (
      <div className="rating-distribution mb-4">
        <h5>Rating Distribution</h5>
        {[5, 4, 3, 2, 1].map((rating) => {
          const count = aggregation.rating_distribution[rating] || 0;
          const percentage = (count / aggregation.total_reviews) * 100;

          return (
            <div key={rating} className="d-flex align-items-center mb-2">
              <span className="me-2" style={{ width: '60px' }}>{rating} stars</span>
              <div className="progress flex-grow-1 me-2" style={{ height: '20px' }}>
                <div
                  className="progress-bar bg-warning"
                  role="progressbar"
                  style={{ width: `${percentage}%` }}
                  aria-valuenow={percentage}
                  aria-valuemin="0"
                  aria-valuemax="100"
                ></div>
              </div>
              <span style={{ width: '40px' }}>{count}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // One card for a multi-dish visit: shared text/author/date/meal once, then each
  // dish as a row with its own rating + photos (#110). Rendered by the shared
  // VisitReviewGroupCard (#116) so the restaurant/food list and the public profile
  // stay identical. The single-dish and standalone cases keep the card below.
  const renderVisitGroupCard = (group) => (
    <VisitReviewGroupCard
      key={`visit-${group[0].visit_id}`}
      group={group}
      onReportReview={openReportModal}
      onReportPhoto={handleReportPhoto}
      currentUser={user}
      token={token}
    />
  );

  if (loading) {
    return <div>Loading reviews...</div>;
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <div className="reviews-section">
      <h3>Reviews</h3>

      {reportSuccess && (
        <Alert variant="success" dismissible onClose={() => setReportSuccess('')}>
          {reportSuccess}
        </Alert>
      )}

      {aggregation && aggregation.total_reviews > 0 && (
        <div className="review-summary mb-4 p-3 bg-light rounded">
          <div className="d-flex align-items-center mb-3">
            <h2 className="mb-0 me-3">{aggregation.average_rating.toFixed(1)}</h2>
            <div>
              {renderStars(Math.round(aggregation.average_rating))}
              <div className="text-muted">
                Based on {aggregation.total_reviews} review{aggregation.total_reviews !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
          {renderRatingDistribution()}
        </div>
      )}

      {reviews.length === 0 ? (
        <p className="text-muted">No reviews yet. Be the first to review!</p>
      ) : (
        <div className="reviews-list">
          {groupReviewsByVisit(reviews).map((group) => {
            if (group.length > 1) return renderVisitGroupCard(group);
            const review = group[0];
            return (
            <Card key={review.id} className="mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    {renderStars(review.rating)}
                    {review.title && <h5 className="mt-2">{review.title}</h5>}
                  </div>
                  {token && user && review.user_id !== user.id && (
                    <Button
                      variant="link"
                      size="sm"
                      className="text-muted p-0"
                      title="Report this review"
                      onClick={() => openReportModal(review)}
                    >
                      ⚑ Report
                    </Button>
                  )}
                </div>

                {review.review_text && (
                  <p className="mt-2" style={{ whiteSpace: 'pre-line' }}>{review.review_text}</p>
                )}

                {/* Public visit photos (#91), shown to everyone once the owner
                    opted in. Click = full size; signed-in non-owners can report. */}
                {review.photos && review.photos.length > 0 && (
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {review.photos.map((photo) => (
                      // #198: framed to the dish this review is for, when the
                      // photo carries a crop for it. A review with no dish
                      // (visit_dish_id null) shows the whole shot, as before.
                      <DishPhoto
                        key={photo.id}
                        photo={photo}
                        visitDishId={review.visit_dish_id}
                        alt="Visit"
                      >
                        {token && user && review.user_id !== user.id && (
                          <Button
                            variant="link"
                            size="sm"
                            className="text-muted p-0 position-absolute top-0 end-0"
                            title="Report this photo"
                            style={{ lineHeight: 1, background: 'rgba(255,255,255,0.85)', borderRadius: '4px' }}
                            onClick={(e) => { e.stopPropagation(); handleReportPhoto(photo.id); }}
                          >
                            ⚑
                          </Button>
                        )}
                      </DishPhoto>
                    ))}
                  </div>
                )}

                {/* Meal + visited location from the linked visit (#65), when captured (#41). */}
                {(review.meal_type || review.location_name || review.location_city || review.location_address) && (
                  <div className="mt-2 text-muted small d-flex flex-wrap gap-3">
                    {review.meal_type && (
                      <span>🍽️ {review.meal_type.charAt(0).toUpperCase() + review.meal_type.slice(1)}</span>
                    )}
                    {(review.location_name || review.location_city || review.location_address) && (
                      <span>📍 {[
                        review.location_name || review.location_address,
                        [review.location_city, review.location_state].filter(Boolean).join(', ')
                      ].filter(Boolean).join(' — ')}</span>
                    )}
                  </div>
                )}

                <div className="d-flex align-items-center mt-3 text-muted small">
                  <span className="me-3">
                    By: <span
                      style={{ cursor: 'pointer', color: '#0d6efd', textDecoration: 'underline' }}
                      onClick={() => navigate(`/profile/${review.user_id}`)}
                    >
                      {/* #171: never fall back to the reviewer's email — it's PII
                          and the backend now withholds it from everyone but the
                          author and admins. Matches the iOS fallback. */}
                      {review.user_display_name || 'Anonymous'}
                    </span>
                    {review.user_is_verified && (
                      <Badge bg="success" className="ms-2">Verified</Badge>
                    )}
                  </span>
                  <span className="me-3">
                    {review.visit_date ? formatDate(review.visit_date) : formatDate(review.created_at)}
                  </span>
                  {review.updated_at && review.updated_at !== review.created_at && (
                    <span className="text-muted fst-italic">
                      (Edited: {formatDate(review.updated_at)})
                    </span>
                  )}
                </div>

                {/* "Who's saying it" (#102 rung-3): whose opinion matters more than
                    how many. Taste-match shows only to a signed-in viewer whose
                    saved/visited spots overlap the reviewer's. */}
                {(review.reviewer_taste_match || review.reviewer_expertise_count > 0) && (
                  <div className="mt-2 d-flex flex-wrap align-items-center gap-2">
                    {review.reviewer_taste_match && (
                      <Badge bg="info" text="dark" title="Their saved & visited spots overlap yours">
                        ✓ Taste like yours
                      </Badge>
                    )}
                    {review.reviewer_expertise_count > 0 && (
                      <span className="text-muted small">
                        Reviewed {review.reviewer_expertise_count}{' '}
                        {review.reviewer_expertise_label || 'similar'} dish{review.reviewer_expertise_count !== 1 ? 'es' : ''}
                      </span>
                    )}
                  </div>
                )}

                {review.helpful_count > 0 && (
                  <div className="mt-2">
                    <Badge bg="secondary">{review.helpful_count} found this helpful</Badge>
                  </div>
                )}
              </Card.Body>
            </Card>
            );
          })}
        </div>
      )}

      <Modal show={!!reportTarget} onHide={() => setReportTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Report Review</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reportError && <Alert variant="danger">{reportError}</Alert>}
          <p className="text-muted small">
            Reported reviews are hidden from public view immediately and reviewed by moderators.
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Reason</Form.Label>
            <Form.Select
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
            >
              {REPORT_REASONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Details (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              maxLength={2000}
              value={reportDetails}
              onChange={(e) => setReportDetails(e.target.value)}
              placeholder="Anything the moderators should know"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setReportTarget(null)} disabled={reportSubmitting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleReportSubmit} disabled={reportSubmitting}>
            {reportSubmitting ? 'Reporting...' : 'Submit Report'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ReviewList;
